import { Injectable } from '@angular/core'
import { forkJoin, map, Observable, of, switchMap } from 'rxjs'
import { getTodayDateTime } from '../utils/credit.utils'
import { CreditService } from './../../api/credit/credit.service'
import { CreditThumbnailService } from './../services/credit-thumbnail.service'
import { BackendService } from '../../api/backend/backend.service'
import { ApiResponse } from '../../api/model/api.model'
import { mergeApiResponses } from '../../api/util'
import {
  CreateSubmissionDraftProps,
  CreditSubmissionStatus,
  CreditSubmissionStructure,
  CreditSubmissionUserTranche,
  CreditSubmissionUserTrancheInputs,
  CreditSubmissionUserTrancheStatus,
} from '../model/credit-submission.model'
import {
  CreateGseSubmissionDraftProps,
  GseSubmissionStatus,
  GseSubmissionStructure,
  GseSubmissionUserTranche,
} from '../model/gse-submission.model'
import { CreditCalculationStructure } from '../model/credit-structure.model'
import { CreditTransactionType } from '../model/credit-layer.model'

@Injectable({
  providedIn: 'root',
})
export class CreditSubmissionService {
  constructor(
    private creditService: CreditService,
    private creditThumbnailService: CreditThumbnailService,
    private backendService: BackendService,
  ) {}

  createSubmissionDraft(props: CreateSubmissionDraftProps): Observable<CreditSubmissionStructure> {
    return this.createCalculationStructure(props.calculationStructure).pipe(
      switchMap((calculationStructure) =>
        this.createSubmissionStructure(calculationStructure, props),
      ),
    )
  }

  createGseSubmissionDraft(
    props: CreateGseSubmissionDraftProps,
  ): Observable<GseSubmissionStructure> {
    return this.createGseCalculationStructure(props.calculationStructure).pipe(
      switchMap((calculationStructure) =>
        this.createGseSubmissionRecord(calculationStructure, props),
      ),
    )
  }

  saveSubmissionUserTranchesForClient(
    userIds: number[],
    calculationStructure: CreditCalculationStructure,
    submissionStructure: CreditSubmissionStructure,
  ): Observable<CreditSubmissionUserTranche[]> {
    const requests = userIds.flatMap((userId) =>
      this.getSubmissionUserTrancheInputs(calculationStructure)
        .filter((t) => t.transactionId === submissionStructure.transaction_id)
        .map((trancheInput) =>
          this.creditService.saveCreditSubmissionUserTranche({
            submission_id: submissionStructure.id!,
            user_id: userId,
            tranche_id: trancheInput.trancheId,
            rol: trancheInput.defaultRol,
            reinsurer_participation_percent: trancheInput.defaulReinsurerParticipationPercent,
            collateral_percent: trancheInput.defaultCollateralPercent,
            ceding_commission: trancheInput.defaultCedingComission,
            profit_commission: trancheInput.defaultProfitComission,
            margin: trancheInput.defaultMargin,
            status: CreditSubmissionUserTrancheStatus.Granted,
            rorac_scenario: trancheInput.defaultRoracScenario,
          }),
        ),
    )
    return forkJoin(requests).pipe(
      map((responses) =>
        responses.map((res) => res.data).filter((d): d is CreditSubmissionUserTranche => !!d),
      ),
    )
  }

  saveGseSubmissionUserTranchesForClient(
    userIds: number[],
    calculationStructure: CreditCalculationStructure,
    submissionStructure: CreditSubmissionStructure,
  ): ApiResponse<GseSubmissionUserTranche[]> {
    const requests = userIds.flatMap((userId) =>
      this.getSubmissionUserTrancheInputs(calculationStructure)
        .filter((t) => t.transactionId === submissionStructure.transaction_id)
        .map((trancheInput) =>
          this.creditService.saveGseSubmissionUserTranche({
            gse_submission_id: submissionStructure.id!,
            user_id: userId,
            tranche_id: trancheInput.trancheId,
            rol: trancheInput.defaultRol,
            reinsurer_participation_percent: trancheInput.defaulReinsurerParticipationPercent,
            collateral_percent: trancheInput.defaultCollateralPercent,
            ceding_commission: trancheInput.defaultCedingComission,
            profit_commission: trancheInput.defaultProfitComission,
            margin: trancheInput.defaultMargin,
            status: CreditSubmissionUserTrancheStatus.Granted,
            rorac_scenario: trancheInput.defaultRoracScenario,
          }),
        ),
    )
    return forkJoin(requests).pipe(mergeApiResponses<GseSubmissionUserTranche>())
  }

  saveGseSubmissionUserTranchesForReinsurers(
    calculationStructure: CreditCalculationStructure,
    submissionStructure: CreditSubmissionStructure,
  ): ApiResponse<GseSubmissionUserTranche[]> {
    if (
      (submissionStructure.addedUserIds?.length ?? 0) === 0 &&
      (submissionStructure.removedUserIds?.length ?? 0) === 0
    ) {
      return of({ data: [] as GseSubmissionUserTranche[] })
    }
    const confirmedUserRequests = (submissionStructure.addedUserIds ?? []).flatMap((userId) =>
      this.getSubmissionUserTrancheInputs(calculationStructure)
        .filter((t) => t.transactionId === submissionStructure.transaction_id)
        .map((trancheInput) =>
          this.creditService.saveGseSubmissionUserTranche({
            gse_submission_id: submissionStructure.id!,
            user_id: userId,
            tranche_id: trancheInput.trancheId,
            rol: trancheInput.defaultRol,
            reinsurer_participation_percent: trancheInput.defaulReinsurerParticipationPercent,
            collateral_percent: trancheInput.defaultCollateralPercent,
            ceding_commission: trancheInput.defaultCedingComission,
            profit_commission: trancheInput.defaultProfitComission,
            margin: trancheInput.defaultMargin,
            status: CreditSubmissionUserTrancheStatus.Granted,
            rorac_scenario: trancheInput.defaultRoracScenario,
          }),
        ),
    )
    const removedUserRequests = (submissionStructure.removedUserIds ?? []).flatMap((userId) =>
      this.getSubmissionUserTrancheInputs(calculationStructure)
        .filter((t) => t.transactionId === submissionStructure.transaction_id)
        .map((trancheInput) =>
          this.creditService.saveGseSubmissionUserTranche({
            gse_submission_id: submissionStructure.id!,
            user_id: userId,
            tranche_id: trancheInput.trancheId,
            rol: trancheInput.defaultRol,
            reinsurer_participation_percent: trancheInput.defaulReinsurerParticipationPercent,
            collateral_percent: trancheInput.defaultCollateralPercent,
            ceding_commission: trancheInput.defaultCedingComission,
            profit_commission: trancheInput.defaultProfitComission,
            margin: trancheInput.defaultMargin,
            status: CreditSubmissionUserTrancheStatus.Cancelled,
            rorac_scenario: trancheInput.defaultRoracScenario,
          }),
        ),
    )
    const requests = [...confirmedUserRequests, ...removedUserRequests]
    return forkJoin(requests).pipe(mergeApiResponses<GseSubmissionUserTranche>())
  }

  saveSubmissionUserTranchesForReinsurers(
    calculationStructure: CreditCalculationStructure,
    submissionStructure: CreditSubmissionStructure,
  ): Observable<CreditSubmissionUserTranche[]> {
    if (
      (submissionStructure.addedUserIds?.length ?? 0) === 0 &&
      (submissionStructure.removedUserIds?.length ?? 0) === 0
    ) {
      return of([])
    }
    const confirmedUserRequests = (submissionStructure.addedUserIds ?? []).flatMap((userId) =>
      this.getSubmissionUserTrancheInputs(calculationStructure)
        .filter((t) => t.transactionId === submissionStructure.transaction_id)
        .map((trancheInput) =>
          this.creditService.saveCreditSubmissionUserTranche({
            submission_id: submissionStructure.id!,
            user_id: userId,
            tranche_id: trancheInput.trancheId,
            rol: trancheInput.defaultRol,
            reinsurer_participation_percent: trancheInput.defaulReinsurerParticipationPercent,
            collateral_percent: trancheInput.defaultCollateralPercent,
            ceding_commission: trancheInput.defaultCedingComission,
            profit_commission: trancheInput.defaultProfitComission,
            margin: trancheInput.defaultMargin,
            status: CreditSubmissionUserTrancheStatus.Granted,
            rorac_scenario: trancheInput.defaultRoracScenario,
          }),
        ),
    )
    const removedUserRequests = (submissionStructure.removedUserIds ?? []).flatMap((userId) =>
      this.getSubmissionUserTrancheInputs(calculationStructure)
        .filter((t) => t.transactionId === submissionStructure.transaction_id)
        .map((trancheInput) =>
          this.creditService.saveCreditSubmissionUserTranche({
            submission_id: submissionStructure.id!,
            user_id: userId,
            tranche_id: trancheInput.trancheId,
            rol: trancheInput.defaultRol,
            reinsurer_participation_percent: trancheInput.defaulReinsurerParticipationPercent,
            collateral_percent: trancheInput.defaultCollateralPercent,
            ceding_commission: trancheInput.defaultCedingComission,
            profit_commission: trancheInput.defaultProfitComission,
            margin: trancheInput.defaultMargin,
            status: CreditSubmissionUserTrancheStatus.Cancelled,
            rorac_scenario: trancheInput.defaultRoracScenario,
          }),
        ),
    )
    const requests = [...confirmedUserRequests, ...removedUserRequests]
    return forkJoin(requests).pipe(
      map((responses) =>
        responses.map((res) => res.data).filter((d): d is CreditSubmissionUserTranche => !!d),
      ),
    )
  }

  private createCalculationStructure(
    calculationStructure: CreditCalculationStructure,
  ): Observable<CreditCalculationStructure> {
    return this.creditService
      .createSubmissionStructure(calculationStructure)
      .pipe(map((res) => res.data!))
  }

  private createGseCalculationStructure(
    calculationStructure: CreditCalculationStructure,
  ): Observable<CreditCalculationStructure> {
    return this.creditService
      .createGseSubmissionStructure(calculationStructure)
      .pipe(map((res) => res.data!))
  }

  private createGseSubmissionRecord(
    calculationStructure: CreditCalculationStructure,
    props: CreateGseSubmissionDraftProps,
  ): Observable<GseSubmissionStructure> {
    this.saveThumbnailForStructure(props, calculationStructure)
    return this.creditService
      .postGseSubmissionStructure({
        program_id: props.creditStructure.program_id,
        label: calculationStructure.name,
        description: props.description,
        gse_submission_structure_id: calculationStructure._id!,
        gse_structure_id: props.creditStructure.id!,
        transaction_id: props.transactionId,
        status: GseSubmissionStatus.Draft,
        selected_gls: props.portfolio.gross_loss_scenarios.map((gls) => gls._id).join(','),
        portfolio_id: props.portfolio._id!,
        doc_link: '', // initially empty
      })
      .pipe(map((res) => res.data!))
  }

  private createSubmissionStructure(
    calculationStructure: CreditCalculationStructure,
    props: CreateSubmissionDraftProps,
  ): Observable<CreditSubmissionStructure> {
    this.saveThumbnailForStructure(props, calculationStructure)
    return this.creditService
      .postCreditSubmissionStructure({
        program_id: props.creditStructure.program_id,
        label: calculationStructure.name,
        description: props.description,
        credit_submission_structure_id: calculationStructure._id!,
        credit_structure_id: props.creditStructure.id!,
        transaction_id: props.transactionId,
        status: CreditSubmissionStatus.Draft,
        selected_gls: props.portfolio.gross_loss_scenarios.map((gls) => gls._id).join(','),
        portfolio_id: props.portfolio._id!,
        doc_link: '', // initially empty
      })
      .pipe(map((res) => res.data!))
  }

  private saveThumbnailForStructure(
    props: CreateSubmissionDraftProps,
    calculationStructure: CreditCalculationStructure,
  ): void {
    const timestamp = getTodayDateTime() // used to bust cache
    if (!props.creditStructure.imageSrc) {
      return
    }
    this.backendService
      .getThumbnailImage(props.creditStructure.imageSrc, timestamp)
      .subscribe((data: Blob) => {
        this.creditThumbnailService
          .saveCreditStructureThumbnail(props.portfolio._id!, calculationStructure._id!, data)
          .subscribe()
      })
  }

  private getSubmissionUserTrancheInputs(
    calculationStructure: CreditCalculationStructure,
  ): CreditSubmissionUserTrancheInputs[] {
    return calculationStructure.transactions.flatMap((transaction) =>
      transaction.tranches.map((tranche) => {
        return {
          transactionId: transaction._id,
          trancheId:
            transaction.transaction_type !== CreditTransactionType.pmiQs
              ? (tranche._id ?? transaction._id)
              : transaction._id,
          defaultRol:
            transaction.transaction_type !== CreditTransactionType.pmiQs ? tranche.rol : 0,
          defaulReinsurerParticipationPercent: tranche.reinsurer_participation_percent ?? 0,
          defaultCollateralPercent: calculationStructure.collateral_percent ?? 0,
          defaultCedingComission:
            transaction.transaction_type === CreditTransactionType.pmiQs
              ? (transaction.ceding_commission ?? 0.3)
              : 0,
          defaultProfitComission:
            transaction.transaction_type === CreditTransactionType.pmiQs
              ? (transaction.profit_commission ?? 0)
              : 0,
          defaultMargin:
            transaction.transaction_type === CreditTransactionType.pmiQs
              ? (transaction.margin ?? 0)
              : 0,
          defaultRoracScenario: calculationStructure.rorac_scenario ?? '',
        }
      }),
    )
  }
}
